import { create } from 'zustand';
import { SettingsSchema, type Language, type Settings } from '@il-colpo/shared/settings';

export const SETTINGS_STORAGE_KEY = 'ilcolpo.settings';

export function detectLanguage(): Language {
  const lang = typeof navigator === 'undefined' ? 'en' : navigator.language;
  return lang.toLowerCase().startsWith('it') ? 'it' : 'en';
}

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (raw) {
      const parsed = SettingsSchema.safeParse(JSON.parse(raw));
      if (parsed.success) return parsed.data;
    }
  } catch {
    // Storage unavailable or corrupt (private mode, blocked cookies): fall back to defaults.
  }
  return SettingsSchema.parse({ language: detectLanguage() });
}

function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage unavailable: settings last for this session only.
  }
}

interface SettingsState {
  settings: Settings;
  /** Mutates a draft copy, validates it with the schema, persists it and publishes it. */
  update: (recipe: (draft: Settings) => void) => void;
  reset: () => void;
}

export const useSettingsStore = create<SettingsState>((set) => ({
  settings: loadSettings(),
  update: (recipe) => {
    set((state) => {
      const draft = structuredClone(state.settings);
      recipe(draft);
      const next = SettingsSchema.parse(draft);
      saveSettings(next);
      return { settings: next };
    });
  },
  reset: () => {
    const next = SettingsSchema.parse({ language: detectLanguage(), firstRunDone: true });
    saveSettings(next);
    set({ settings: next });
  },
}));

export const useSettings = (): Settings => useSettingsStore((s) => s.settings);
